import { useEffect, useRef, useState } from 'react';
import { TruncatedInputLabel } from 'styles/InputStyles';
import { IOptions } from 'utils/types';

import {
  CircularProgress,
  FormControl,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Tooltip,
} from '@mui/material';

import { ListItemTextOption, PlaceholderText } from './CustomSelect.styles';

interface ICustomSelectProps {
  setValue: (value: string | number) => void;
  value: string | number | undefined;
  options: IOptions[];
  label: string;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  name: string;
  id: string;
}

const CustomSelect = (props: ICustomSelectProps) => {
  const handleChange = (event: SelectChangeEvent<unknown>) => {
    const selectedValues = event.target.value as any;
    props.setValue(selectedValues);
  };

  // Truncamento da label
  const spanRef = useRef<HTMLLabelElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (spanRef.current) {
        setIsTruncated(
          spanRef.current.scrollWidth > spanRef.current.clientWidth,
        );
      }
    };
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [props.label]);

  return (
    <FormControl fullWidth variant="filled">
      <Tooltip
        title={isTruncated ? props.label : ''}
        arrow
        disableHoverListener={!isTruncated}
        placement="top"
      >
        <TruncatedInputLabel
          htmlFor={props.id}
          data-testid={`label-${props.name}`}
        >
          <span ref={spanRef} className="label-text">
            {props.label}
          </span>
          {props.required && !props.disabled && (
            <span className="required-asterisk">*</span>
          )}
        </TruncatedInputLabel>
      </Tooltip>

      <TextField
        select
        variant="filled"
        size="small"
        hiddenLabel
        fullWidth
        data-testid={`select-${props.name}`}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        defaultValue=""
        error={props.error}
        helperText={props.helperText}
        required={props.required}
        InputProps={{
          endAdornment: props.loading ? (
            <CircularProgress size={16} />
          ) : undefined,
        }}
        slotProps={{
          select: {
            value: props.value,
            onChange: handleChange,
            displayEmpty: true,
            renderValue: (selected: unknown) => {
              const selectedValue = selected as number | string;
              if (selectedValue === '') {
                return <PlaceholderText>{props.placeholder}</PlaceholderText>;
              } else {
                return (
                  <PlaceholderText>
                    {
                      props.options.find(
                        (option) => option.value === selectedValue,
                      )?.label
                    }
                  </PlaceholderText>
                );
              }
            },
          },
        }}
      >
        {props.options.map((option: IOptions) => (
          <MenuItem
            key={option.label}
            value={option.value}
            disabled={option.disabled}
          >
            <ListItemTextOption>{option.label}</ListItemTextOption>
          </MenuItem>
        ))}
      </TextField>
    </FormControl>
  );
};

export default CustomSelect;
